/**
 * Build queue — spec §9.
 *
 * Head-of-line blocking is only defensible if the stall explains itself, so the
 * starved banner is the most prominent thing here: it names the reason, the
 * missing goods, and the projected wait. §9 is explicit that blocking without
 * that is "opaque and infuriating".
 *
 * Only the first slice of the queue is rendered. The player can legitimately
 * queue all 616 plots on turn one, and 616 draggable rows rebuilt on every tick
 * would be both unreadable and slow.
 */

import { BUILDINGS, GOODS, GOOD_IDS } from '../../sim/data';
import { committedCost } from '../../sim/queue';
import type { GameState } from '../../sim/types';
import { compact, el, section, setText, type Issue, type Panel } from './panel';

/** Rows rendered at once. Beyond this the queue is summarised as a count. */
const VISIBLE_ROWS = 30;

export function createQueuePanel(issue: Issue): Panel {
  const { root, body } = section('Build queue');

  const starved = el('div', 'starved');
  const summary = el('div', 'hint');
  const committed = el('div', 'committed');
  const list = el('div', 'queue-list');
  const overflow = el('div', 'hint');

  body.append(starved, summary, committed, list, overflow);

  // Drag state lives here rather than on the elements, so a row removed
  // mid-drag cannot leave a stale index behind.
  let dragFrom: number | null = null;

  interface Row {
    root: HTMLDivElement;
    label: HTMLSpanElement;
    cancel: HTMLButtonElement;
  }

  const rows: Row[] = [];

  for (let index = 0; index < VISIBLE_ROWS; index++) {
    const rowRoot = el('div', 'queue-row');
    rowRoot.draggable = true;

    const label = el('span', 'row-label');
    const cancel = el('button', 'action', '×');
    cancel.title = 'Cancel — queued plots cost nothing to drop';
    cancel.addEventListener('click', (event) => {
      event.stopPropagation();
      issue({ type: 'cancelQueued', index });
    });

    rowRoot.addEventListener('dragstart', () => {
      dragFrom = index;
      rowRoot.classList.add('dragging');
    });
    rowRoot.addEventListener('dragend', () => {
      dragFrom = null;
      rowRoot.classList.remove('dragging');
    });
    rowRoot.addEventListener('dragover', (event) => {
      event.preventDefault();
      rowRoot.classList.add('drop-target');
    });
    rowRoot.addEventListener('dragleave', () => {
      rowRoot.classList.remove('drop-target');
    });
    rowRoot.addEventListener('drop', (event) => {
      event.preventDefault();
      rowRoot.classList.remove('drop-target');
      if (dragFrom !== null && dragFrom !== index) {
        issue({ type: 'reorderQueue', from: dragFrom, to: index });
      }
      dragFrom = null;
    });

    rowRoot.append(label, cancel);
    rows.push({ root: rowRoot, label, cancel });
    list.append(rowRoot);
  }

  return {
    root,
    update(state: GameState): void {
      const queue = state.buildQueue;

      setText(summary, queue.length === 0 ? 'Queue empty.' : `${queue.length} queued`);

      // --- starved banner ---
      if (state.starved === null) {
        starved.className = 'starved';
        setText(starved, '');
      } else {
        const info = state.starved;
        starved.className = 'starved active';

        if (info.reason === 'mechs') {
          setText(starved, 'Waiting on mechs — every mech is already on a site.');
        } else if (info.reason === 'disconnected') {
          setText(
            starved,
            'Head plot is cut off from the city. Mechs cannot reach it — cancel it, ' +
              'or queue a plot connecting it back.',
          );
        } else {
          const missing = GOOD_IDS.filter((id) => (info.missing[id] ?? 0) > 0)
            .map((id) => `${info.missing[id]} ${GOODS[id].label}`)
            .join(', ');
          const wait =
            info.projectedWaitTicks === null
              ? 'not on current production'
              : `~${info.projectedWaitTicks} ticks`;
          setText(starved, `Starved: needs ${missing} — ${wait}`);
        }
      }

      // --- committed cost ---
      if (queue.length === 0) {
        setText(committed, '');
      } else {
        const cost = committedCost(state);
        const parts = GOOD_IDS.filter((id) => cost[id] > 0).map((id) => {
          const have = state.inventory.goods[id];
          return `${GOODS[id].label} ${compact(have)}/${compact(cost[id])}`;
        });
        setText(committed, `Committed: ${parts.join('  ')}`);
      }

      // --- rows ---
      for (let index = 0; index < VISIBLE_ROWS; index++) {
        const row = rows[index];
        if (row === undefined) continue;
        const item = queue[index];

        if (item === undefined) {
          row.root.classList.add('hidden');
          continue;
        }

        row.root.classList.remove('hidden');
        row.root.classList.toggle('head', index === 0);
        setText(
          row.label,
          `${index + 1}. ${BUILDINGS[item.building].label} (${item.plot.x},${item.plot.y})`,
        );
      }

      setText(
        overflow,
        queue.length > VISIBLE_ROWS ? `…and ${queue.length - VISIBLE_ROWS} more` : '',
      );
    },
  };
}
